/**
 * Gera PNG da tabela via Canvas2D para compartilhamento (WhatsApp).
 *
 * Implementação direta em canvas em vez de html-to-image — biblioteca
 * tinha falhas intermitentes capturando tabelas grandes (saída em branco
 * com 250+ linhas). Canvas é determinístico, sem dependência de DOM
 * offscreen, e suporta o mesmo padrão visual: header navy, zebra rows,
 * coluna de status colorida por `_row_color`/`row_color_columns`.
 */

import type { TableData } from '../types/tableData'

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
const HEADER_BG = '#03154a'
const HEADER_FG = '#ffffff'
const ROW_BG_EVEN = '#ffffff'
const ROW_BG_ODD = '#f8f9fa'
const ROW_FG = '#1f2937'
const BORDER = '#e5e7eb'
const TITLE_FG = '#03154a'
const SUBTITLE_FG = '#6b7280'

const FONT_SIZE = 14
const FONT_HEADER = `600 ${FONT_SIZE}px ${FONT_FAMILY}`
const FONT_CELL = `${FONT_SIZE}px ${FONT_FAMILY}`
const FONT_TITLE = `700 22px ${FONT_FAMILY}`
const FONT_SUBTITLE = `13px ${FONT_FAMILY}`

const PAD_X = 14
const PAD_Y = 10
const ROW_H_DEFAULT = FONT_SIZE + PAD_Y * 2
const WRAPPER_PAD = 24

// Limites portáveis de canvas:
// - iOS Safari trava em ~16 MP de área total e ~4096 px por dimensão em
//   versões antigas; navegadores desktop suportam até ~16384 px em uma
//   dimensão. Acima disso `toBlob()` falha silenciosamente (PNG em branco).
const SAFE_MAX_DIM = 16384
const SAFE_MAX_AREA = 16_777_216

function formatBrl(value: number): string {
  return value
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

function formatCell(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === '') return '—'
  if (format === 'currency_brl' && typeof value === 'number') {
    return `R$ ${formatBrl(value)}`
  }
  return String(value)
}

function measureColumns(
  ctx: CanvasRenderingContext2D,
  data: TableData
): { widths: number[]; total: number } {
  const cols = data.export_columns ?? data.columns
  const widths = cols.map(col => {
    ctx.font = FONT_HEADER
    let max = ctx.measureText(col.label).width
    ctx.font = FONT_CELL
    for (const row of data.rows) {
      const text = formatCell(row[col.key], col.format)
      const w = ctx.measureText(text).width
      if (w > max) max = w
    }
    return Math.ceil(max) + PAD_X * 2
  })
  const total = widths.reduce((a, b) => a + b, 0)
  return { widths, total }
}

export async function generateTablePngBlob(data: TableData): Promise<Blob> {
  const cols = data.export_columns ?? data.columns
  const colorField = data.row_color_field
  const colorColumns = new Set(data.row_color_columns ?? [])
  const cellLevelColoring = colorColumns.size > 0

  // Pré-medição usando canvas temporário (mesmo contexto que será usado).
  const probe = document.createElement('canvas')
  const probeCtx = probe.getContext('2d')!
  const { widths, total: tableWidth } = measureColumns(probeCtx, data)

  // Tamanho do título/subtítulo
  probeCtx.font = FONT_TITLE
  const titleW = probeCtx.measureText(data.title || 'Relatório').width
  probeCtx.font = FONT_SUBTITLE
  const subW = data.subtitle ? probeCtx.measureText(data.subtitle).width : 0
  const headerWidth = Math.max(tableWidth, titleW, subW)

  // Altura: padding + título + (subtítulo) + gap + header_row + n linhas
  const titleH = 28
  const subH = data.subtitle ? 20 : 0
  const gap = 12
  const totalWidth = WRAPPER_PAD * 2 + headerWidth
  const totalRows = 1 + data.rows.length

  // Calcula ROW_H/dpr que mantém o canvas dentro de SAFE_MAX_DIM/SAFE_MAX_AREA.
  // Estratégia:
  //   1. Tenta dpr=2 com ROW_H_DEFAULT.
  //   2. Se exceder, cai pra dpr=1.
  //   3. Se mesmo com dpr=1 a altura passar de SAFE_MAX_DIM, reduz ROW_H até
  //      caber (piso de 22 px = legível). Sem piso a tabela fica ilegível e
  //      é melhor um PNG truncado com aviso visual do que branco silencioso.
  const overheadH = WRAPPER_PAD * 2 + titleH + subH + gap
  const fits = (dpr: number, rowH: number) => {
    const h = overheadH + rowH * totalRows
    const cw = Math.ceil(totalWidth * dpr)
    const ch = Math.ceil(h * dpr)
    return cw <= SAFE_MAX_DIM && ch <= SAFE_MAX_DIM && cw * ch <= SAFE_MAX_AREA
  }
  let dpr = 2
  let ROW_H = ROW_H_DEFAULT
  if (!fits(dpr, ROW_H)) dpr = 1
  while (!fits(dpr, ROW_H) && ROW_H > 22) ROW_H -= 1
  const tableHeight = ROW_H * totalRows
  const totalHeight = overheadH + tableHeight

  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(totalWidth * dpr)
  canvas.height = Math.ceil(totalHeight * dpr)
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  // Fundo
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, totalWidth, totalHeight)

  // Título
  let y = WRAPPER_PAD
  ctx.fillStyle = TITLE_FG
  ctx.font = FONT_TITLE
  ctx.textBaseline = 'top'
  ctx.fillText(data.title || 'Relatório', WRAPPER_PAD, y)
  y += titleH

  if (data.subtitle) {
    ctx.fillStyle = SUBTITLE_FG
    ctx.font = FONT_SUBTITLE
    ctx.fillText(data.subtitle, WRAPPER_PAD, y)
    y += subH
  }
  y += gap

  // Header da tabela
  const tableLeft = WRAPPER_PAD
  ctx.fillStyle = HEADER_BG
  ctx.fillRect(tableLeft, y, tableWidth, ROW_H)
  ctx.fillStyle = HEADER_FG
  ctx.font = FONT_HEADER
  let x = tableLeft
  for (let i = 0; i < cols.length; i++) {
    const col = cols[i]
    const align = col.align || 'left'
    const text = col.label
    const tw = ctx.measureText(text).width
    let textX: number
    if (align === 'right') textX = x + widths[i] - PAD_X - tw
    else if (align === 'center') textX = x + (widths[i] - tw) / 2
    else textX = x + PAD_X
    ctx.fillText(text, textX, y + PAD_Y)
    x += widths[i]
  }
  y += ROW_H

  // Linhas
  ctx.font = FONT_CELL
  for (let i = 0; i < data.rows.length; i++) {
    const row = data.rows[i]
    const rowBg = i % 2 === 0 ? ROW_BG_EVEN : ROW_BG_ODD
    ctx.fillStyle = rowBg
    ctx.fillRect(tableLeft, y, tableWidth, ROW_H)

    const rowColor = colorField
      ? (row[colorField] as string | undefined)
      : undefined
    const validHex = !!(rowColor && /^#[0-9A-Fa-f]{6}$/.test(rowColor))

    x = tableLeft
    for (let j = 0; j < cols.length; j++) {
      const col = cols[j]
      const applyColor =
        validHex && (!cellLevelColoring || colorColumns.has(col.key))
      if (applyColor) {
        ctx.fillStyle = rowColor as string
        ctx.fillRect(x, y, widths[j], ROW_H)
      }
      ctx.fillStyle = ROW_FG
      const align = col.align || 'left'
      const text = formatCell(row[col.key], col.format)
      const tw = ctx.measureText(text).width
      let textX: number
      if (align === 'right') textX = x + widths[j] - PAD_X - tw
      else if (align === 'center') textX = x + (widths[j] - tw) / 2
      else textX = x + PAD_X
      ctx.fillText(text, textX, y + PAD_Y)
      x += widths[j]
    }
    // Linha de baixo
    ctx.strokeStyle = BORDER
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(tableLeft, y + ROW_H + 0.5)
    ctx.lineTo(tableLeft + tableWidth, y + ROW_H + 0.5)
    ctx.stroke()
    y += ROW_H
  }

  // Borda externa
  ctx.strokeStyle = BORDER
  ctx.strokeRect(
    tableLeft + 0.5,
    WRAPPER_PAD + titleH + subH + gap + 0.5,
    tableWidth,
    tableHeight
  )

  return await new Promise<Blob>((res, rej) => {
    canvas.toBlob(
      b => (b ? res(b) : rej(new Error('toBlob falhou'))),
      'image/png'
    )
  })
}

export async function copyTablePngToClipboard(data: TableData): Promise<void> {
  const blob = await generateTablePngBlob(data)
  if (!('ClipboardItem' in window)) {
    throw new Error('Clipboard API não suportada neste navegador')
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ClipboardItemCtor = (window as any).ClipboardItem
  await navigator.clipboard.write([
    new ClipboardItemCtor({ 'image/png': blob }),
  ])
}

export async function downloadTablePng(data: TableData): Promise<void> {
  const blob = await generateTablePngBlob(data)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const slug = (data.title || 'tabela')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16)
  a.download = `${slug}-${ts}.png`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
